import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, retry, switchMap, take, takeUntil } from 'rxjs/operators';

import { RemoteDiagnosticsCloudApiPicker } from '@vi-iot/client';
import { ViToastService } from '@vi-iot/ui';

import { VddDetailsListSelectors } from '@store';
import { AppInsightsMonitoringService } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_HYDRAULIC_SCHEME } from 'src/app/shared/services/app-insights/events/remote-diagnostics.events';
import { LogLevelEnum } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';

import { VddWriteErrorModalComponent } from '../../+installations/diagnostics-overlay/tabs-content/extended-parameters/diagnostics-view/vdd-write-error-modal/vdd-write-error-modal.component';
import { RemoteDiagnosticsHelpersService } from '../../../projects/client/apis/remote-diagnostics/remote-diagnostics-helpers.service';
import { AppState } from '../app.state';
import { backupMainLogicalLink } from '../remote-diagnostics';
import { RemoteDiagnosticsStore } from '../remote-diagnostics/remote-diagnostics.store';

import { removeNestedObjectsImmutable } from '../_common/object-helpers';
import {
  DataKeyValues,
  SemanticValues,
} from '../../+installations/diagnostics-overlay/tabs-content/extended-parameters/diagnostics-constants';
import { VddDetailsListActions } from './vdd-details-list.actions';

@Injectable()
export class VddDetailsListEffects {
  getVddMetadata$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(VddDetailsListActions.GetVddMetadataAttempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getSelectedParametersLogicalLink(action.gatewaySerial, action.vddName)
            .pipe(take(1)),
          this.remoteDiagnosticsStore
            .getMainLogicalLinkFromTopology(action.gatewaySerial)
            .pipe(take(1)),
        ])
      ),
      mergeMap(([action, selectedParameterLogicalLink, mainLogicalLinkFromTopology]) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(action.gatewaySerial)
            .pipe(take(1)),
          of(
            action.logicalLinkName ??
              selectedParameterLogicalLink ?? // available only on parameters view,
              mainLogicalLinkFromTopology ?? // should be available for every session
              backupMainLogicalLink
          ),
        ])
      ),
      mergeMap(([action, diagStackConnectionProperties, logicalLink]) =>
        this.remoteDiagnosticsCloudApiPicker
          .instance()
          .diagnosticInfoVddMetadata(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            diagStackConnectionProperties.vciResource,
            diagStackConnectionProperties.vciName,
            action.vddName,
            logicalLink
          )
          .pipe(
            map((metadata) =>
              VddDetailsListActions.GetVddMetadataSuccess({
                vddName: action.vddName,
                logicalLinkName: logicalLink,
                metadata,
              })
            ),
            catchError((error) =>
              of(
                VddDetailsListActions.GetVddMetadataFailed({
                  vddName: action.vddName,
                  logicalLinkName: logicalLink,
                  error,
                })
              )
            )
          )
      )
    );
  });

  getVddStructure$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(VddDetailsListActions.GetVddStructureAttempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getSelectedParametersLogicalLink(action.gatewaySerial, action.vddName)
            .pipe(take(1)),
          this.remoteDiagnosticsStore
            .getMainLogicalLinkFromTopology(action.gatewaySerial)
            .pipe(take(1)),
        ])
      ),
      mergeMap(([action, selectedParameterLogicalLink, mainLogicalLinkFromTopology]) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(action.gatewaySerial)
            .pipe(take(1)),
          of(
            action.logicalLinkName ??
              selectedParameterLogicalLink ?? // available only on parameters view,
              mainLogicalLinkFromTopology ?? // should be available for every session
              backupMainLogicalLink
          ),
        ])
      ),
      mergeMap(([action, diagStackConnectionProperties, logicalLink]) =>
        this.remoteDiagnosticsCloudApiPicker
          .instance()
          .diagnosticInfoVddStructure(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            diagStackConnectionProperties.vciResource,
            diagStackConnectionProperties.vciName,
            action.vddName,
            logicalLink
          )
          .pipe(
            map((structure) =>
              VddDetailsListActions.GetVddStructureSuccess({
                vddName: action.vddName,
                logicalLinkName: logicalLink,
                structure,
              })
            ),
            catchError((error) =>
              of(
                VddDetailsListActions.GetVddStructureFailed({
                  vddName: action.vddName,
                  logicalLinkName: logicalLink,
                  error,
                })
              )
            ),
            takeUntil(this.remoteDiagnosticsHelpersService.cancelRequests$)
          )
      )
    );
  });

  getVddSingleValue$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(VddDetailsListActions.GetVddSingleValueAttempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getSelectedParametersLogicalLink(action.gatewaySerial, action.vddName)
            .pipe(take(1)),
          this.remoteDiagnosticsStore
            .getMainLogicalLinkFromTopology(action.gatewaySerial)
            .pipe(take(1)),
        ])
      ),
      mergeMap(([action, selectedParameterLogicalLink, mainLogicalLinkFromTopology]) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(action.gatewaySerial)
            .pipe(take(1)),
          of(
            action.logicalLinkName ??
              selectedParameterLogicalLink ?? // available only on parameters view,
              mainLogicalLinkFromTopology ?? // should be available for every session
              backupMainLogicalLink
          ),
        ])
      ),
      mergeMap(([action, diagStackConnectionProperties, logicalLink]) =>
        this.remoteDiagnosticsCloudApiPicker
          .instance()
          .diagnosticServiceDataReadSync(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            logicalLink,
            diagStackConnectionProperties.vciResource,
            diagStackConnectionProperties.vciName,
            action.vddName
          )
          .pipe(
            map((singleRead) =>
              VddDetailsListActions.GetVddSingleValueSuccess({
                vddName: action.vddName,
                logicalLinkName: logicalLink,
                singleRead,
              })
            ),
            catchError((error) => {
              if (action.vddName === 'HydraulicMatrix') {
                this.appInsightsMonitoringService.logEvent(
                  APP_INSIGHTS_HYDRAULIC_SCHEME.GET_HYDRAULIC_MATRIX_CODE_FAILED,
                  LogLevelEnum.ERROR,
                  { error }
                );
              }

              return of(
                VddDetailsListActions.GetVddSingleValueFailed({
                  vddName: action.vddName,
                  logicalLinkName: logicalLink,
                  error,
                })
              );
            }),
            takeUntil(this.remoteDiagnosticsHelpersService.cancelRequests$)
          )
      )
    );
  });

  writeVddSingleValue$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(VddDetailsListActions.writeVddSingleValue.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(action.gatewaySerial)
            .pipe(take(1)),
          this.remoteDiagnosticsStore
            .getSelectedParameterIsReadFlag(
              action.gatewaySerial,
              action.vddName,
              action.logicalLink
            )
            .pipe(take(1)),
          this.store
            .select(
              VddDetailsListSelectors.getVddpayloadForVddIsReadFalse(
                action.vddName,
                action.logicalLink
              )
            )
            .pipe(take(1)),

          this.store
            .select(
              VddDetailsListSelectors.getVddRawSingleReadValues(action.vddName, action.logicalLink)
            )
            .pipe(take(1)),
        ])
      ),
      mergeMap(
        ([
          action,
          diagStackConnectionProperties,
          isVddRead,
          getVddpayloadForVddIsReadFalse,
          vddRawSingleReadValues,
        ]) =>
          this.remoteDiagnosticsCloudApiPicker
            .instance()
            .diagnosticServiceDataWrite(
              diagStackConnectionProperties.node,
              diagStackConnectionProperties.xAuthToken,
              action.logicalLink,
              diagStackConnectionProperties.vciResource,
              diagStackConnectionProperties.vciName,
              isVddRead,
              getVddpayloadForVddIsReadFalse,
              vddRawSingleReadValues
            )
            .pipe(
              retry(2),

              map(() => {
                if (isVddRead) {
                  return VddDetailsListActions.writeVddSingleValue.success({
                    diagStackConnectionProperties,
                    logicalLink: action.logicalLink,
                    vddName: action.vddName,
                    vddRawSingleReadValues,
                  });
                } else {
                  return VddDetailsListActions.verifyWrittenVddSingleValue.successfulVerification({
                    vddName: action.vddName,
                    logicalLink: action.logicalLink,
                  });
                }
              }),
              catchError(() => {
                this.dialog.open(VddWriteErrorModalComponent);

                return of(VddDetailsListActions.writeVddSingleValue.failed());
              })
            )
      )
    );
  });

  prepareVerificationOfWrittenVdd$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(VddDetailsListActions.writeVddSingleValue.success),
      map(({ diagStackConnectionProperties, logicalLink, vddName, vddRawSingleReadValues }) =>
        VddDetailsListActions.verifyWrittenVddSingleValue.attempt({
          diagStackConnectionProperties,
          logicalLink,
          vddName,
          vddRawSingleReadValues,
        })
      )
    );
  });

  verifyWrittenVddSingleValue$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(VddDetailsListActions.verifyWrittenVddSingleValue.attempt),
      switchMap(({ diagStackConnectionProperties, logicalLink, vddName, vddRawSingleReadValues }) =>
        this.remoteDiagnosticsCloudApiPicker
          .instance()
          .diagnosticServiceDataReadSync(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            logicalLink,
            diagStackConnectionProperties.vciResource,
            diagStackConnectionProperties.vciName,
            vddName
          )
          .pipe(
            retry(2),
            map((oldSingleRead) => {
              return {
                isCorrectlyWritten: isEqual(
                  removeNestedObjectsImmutable(
                    oldSingleRead.structure,
                    DataKeyValues.Semantic,
                    SemanticValues.RESERVED
                  ),
                  removeNestedObjectsImmutable(
                    vddRawSingleReadValues.structure,
                    DataKeyValues.Semantic,
                    SemanticValues.RESERVED
                  )
                ),
                oldSingleRead,
              };
            }),
            map(({ isCorrectlyWritten, oldSingleRead }) => {
              if (isCorrectlyWritten) {
                this.toastService.info({
                  message: this.translateService.instant('DIAGNOSTICS.WRITE_VDD.SUCCESS'),
                });

                return VddDetailsListActions.verifyWrittenVddSingleValue.successfulVerification({
                  vddName,
                  logicalLink,
                });
              } else {
                this.dialog.open(VddWriteErrorModalComponent);

                return VddDetailsListActions.verifyWrittenVddSingleValue.failedVerification({
                  vddName,
                  logicalLink,
                  oldSingleRead,
                });
              }
            }),
            catchError((error) => {
              return of(
                VddDetailsListActions.verifyWrittenVddSingleValue.error({
                  vddName,
                  logicalLinkName: logicalLink,
                  error,
                })
              );
            })
          )
      )
    );
  });

  getMetadataAndStructureAndValueForVddListAttempt$: Observable<void> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(VddDetailsListActions.GetMetadataAndStructureAndValueForVddListAttempt),
        mergeMap((action) =>
          combineLatest([
            of(action),
            this.remoteDiagnosticsStore
              .getMainLogicalLinkFromTopology(action.gatewaySerial)
              .pipe(take(1)),
          ])
        ),
        map(([action, logicalLinkFromTopology]) =>
          action.vddNames.forEach((vddName) =>
            this.store.dispatch(
              VddDetailsListActions.GetMetadataAndStructureAndValueAttempt({
                vddName,
                gatewaySerial: action.gatewaySerial,
                logicalLinkName:
                  action.logicalLinkName ?? logicalLinkFromTopology ?? backupMainLogicalLink,
              })
            )
          )
        )
      );
    },
    { dispatch: false }
  );

  getStructureAndValueForVddListAttempt$: Observable<void> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(VddDetailsListActions.GetStructureAndValueForVddListAttempt),
        mergeMap((action) =>
          combineLatest([
            of(action),
            this.remoteDiagnosticsStore
              .getMainLogicalLinkFromTopology(action.gatewaySerial)
              .pipe(take(1)),
          ])
        ),
        map(([action, logicalLinkFromTopology]) =>
          action.vddNames.forEach((vddName) => {
            this.store.dispatch(
              VddDetailsListActions.GetStructureAndValueAttempt({
                vddName,
                gatewaySerial: action.gatewaySerial,
                logicalLinkName:
                  action.logicalLinkName ?? logicalLinkFromTopology ?? backupMainLogicalLink,
              })
            );
          })
        )
      );
    },
    { dispatch: false }
  );

  getStructureAndValue$: Observable<void> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(VddDetailsListActions.GetStructureAndValueAttempt),
        mergeMap((action) => {
          return combineLatest([
            of(action),
            this.remoteDiagnosticsStore
              .getMainLogicalLinkFromTopology(action.gatewaySerial)
              .pipe(take(1)),
          ]);
        }),
        map(([action, logicalLinkFromTopology]) => {
          this.store.dispatch(
            VddDetailsListActions.GetVddStructureAttempt({
              vddName: action.vddName,
              gatewaySerial: action.gatewaySerial,
              logicalLinkName:
                action.logicalLinkName ?? logicalLinkFromTopology ?? backupMainLogicalLink,
            })
          );
          this.store.dispatch(
            VddDetailsListActions.GetVddSingleValueAttempt({
              vddName: action.vddName,
              gatewaySerial: action.gatewaySerial,
              logicalLinkName:
                action.logicalLinkName ?? logicalLinkFromTopology ?? backupMainLogicalLink,
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  getMetadataAndStructureAndValue$: Observable<void> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(VddDetailsListActions.GetMetadataAndStructureAndValueAttempt),
        mergeMap((action) => {
          return combineLatest([
            of(action),
            this.remoteDiagnosticsStore
              .getMainLogicalLinkFromTopology(action.gatewaySerial)
              .pipe(take(1)),
            this.remoteDiagnosticsStore
              .getSelectedParameterIsReadFlag(
                action.gatewaySerial,
                action.vddName,
                action.logicalLinkName
              )
              .pipe(take(1)),
          ]);
        }),
        map(([action, logicalLinkFromTopology, isReadParameter]) => {
          this.store.dispatch(
            VddDetailsListActions.GetVddMetadataAttempt({
              vddName: action.vddName,
              gatewaySerial: action.gatewaySerial,
              logicalLinkName:
                action.logicalLinkName ?? logicalLinkFromTopology ?? backupMainLogicalLink,
            })
          );
          this.store.dispatch(
            VddDetailsListActions.GetVddStructureAttempt({
              vddName: action.vddName,
              gatewaySerial: action.gatewaySerial,
              logicalLinkName:
                action.logicalLinkName ?? logicalLinkFromTopology ?? backupMainLogicalLink,
            })
          );
          if (isReadParameter) {
            this.store.dispatch(
              VddDetailsListActions.GetVddSingleValueAttempt({
                vddName: action.vddName,
                gatewaySerial: action.gatewaySerial,
                logicalLinkName:
                  action.logicalLinkName ?? logicalLinkFromTopology ?? backupMainLogicalLink,
              })
            );
          }
        })
      );
    },
    { dispatch: false }
  );

  constructor(
    private remoteDiagnosticsCloudApiPicker: RemoteDiagnosticsCloudApiPicker,
    private remoteDiagnosticsStore: RemoteDiagnosticsStore,
    private toastService: ViToastService,
    private translateService: TranslateService,
    private dialog: MatDialog,
    private actions$: Actions,
    private store: Store<AppState>,
    private remoteDiagnosticsHelpersService: RemoteDiagnosticsHelpersService,
    private appInsightsMonitoringService: AppInsightsMonitoringService
  ) {}
}
